// LLM generated - Model Claude Opus 4.5
import { IconCheck } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';

import { formatDollars } from '~/domains/common/utils/formatters';
import { Text } from '~/domains/design-system/Text';

import useStyles from './PayWithCreditsToggle.styles';
import type { PayWithCreditsToggleProps } from './types';

function PayWithCreditsToggle({
  checked,
  creditBalance,
  hasSufficientCredits,
  hasVoucher,
  onCheckedChange,
  label = 'Use Credits first',
  disabled = false,
  variant = 'light',
  insufficientCreditsMessage,
}: PayWithCreditsToggleProps) {
  const { t } = useTranslation('common');
  const { classes, cx } = useStyles({ variant });

  // Determine if component should be interactive
  const isDisabled = disabled || !hasSufficientCredits || hasVoucher;
  const canApply = !hasVoucher && (hasSufficientCredits || !insufficientCreditsMessage)
  const showCheckbox = hasSufficientCredits || !insufficientCreditsMessage;

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onCheckedChange(!checked);
    }
  }, [checked, isDisabled, onCheckedChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onCheckedChange(!checked);
      }
    },
    [checked, isDisabled, onCheckedChange]
  );

  return (
    <div
      className={cx(classes.container, {
        [classes.containerDisabled]: isDisabled,
      })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="checkbox"
      aria-checked={hasSufficientCredits ? checked : false}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
    >
      <div className={classes.content}>
        <Text variant="subhead-medium" className={classes.title}>
          {label}
        </Text>
        <Text
          variant="subhead-small"
          className={cx(classes.balance, {
            [classes.balanceInsufficient]: !hasSufficientCredits,
          })}
        >
          {t('balanceUpdate.balance')}: {formatDollars(creditBalance, true)}
        </Text>
      </div>
      {!canApply && (
        <Text variant="body-medium" className={classes.insufficientMessage}>
          {insufficientCreditsMessage}
        </Text>
      )}
      {hasVoucher && (
        <Text variant="subhead-x-small" className={classes.voucherApplied} >
          {t('balanceUpdate.voucherApplied')}
        </Text>
      )}
      {canApply && (
        <div
          className={cx(classes.checkbox, {
            [classes.checkboxChecked]: hasSufficientCredits && checked,
          })}
        >
          {hasSufficientCredits && checked && (
            <IconCheck className={classes.checkIcon} stroke={2.5} />
          )}
        </div>
      )}
    </div>
  );
}

export default PayWithCreditsToggle;
